
import React from 'react';
import { UseQueryResult, useQuery, UseMutationResult, useMutation } from '@tanstack/react-query'
import {
  MasterProvinceResponse,
  MasterDistrictResponse,
  MasterSubDistrictResponse,
  MasterPropertyResponse,
  GetMasterResponseZipCode,
  GetMasterRetailType,
  GetMasterTransportResponse,
  GetMasterLocationResponse,
  GetDatasourceListResponse,
  MasterTransportInput,
  GetMasterShippingBandResponse,
  InputCreateMasterTransport,
  CreateMasterTransportResponse,
  MasterTransportListResponse,
  DeleteMasterTransportResponse,
  InputDeleteMasterTransport,
  UpdateMasterTransportResponse,
  InputUpdateMasterTransport,
  MasterTransportByIdResponse,
  MasterProcessResponse,
  GeographyResponse,
  TagNameResponse,
  AddTagNamesResponse,
  AddTagNamesInput,
  CreateTagNamesInput,
  GetMasterPointInput,
  GetMasterPointResponse,
  CreateMasterPointInput,
  CreateMasterPointResponse,
  UpdateMasterPointInput,
  DeleteMasterPointInput,
  DeleteTagNamesInput,
  MasterBoxResponse,
  MasterBoxSizeResponse,
  CreateMasterData,
  UpdateMasterData,
  // MasterBoxes interfaces (New System)
  GetAllMasterBoxesRequest,
  CreateMasterBoxRequest,
  CreateMasterBoxResponse,
  UpdateMasterBoxRequest,
  UpdateMasterBoxResponse,
  DeleteMasterBoxRequest,
  DeleteMasterBoxResponse,
  MasterBoxSingleResponse,
  MasterPackagingTypeResponse,
  MasterPackagingTypeSingleResponse,
} from 'services/interface'
import {
  getMasterProvice,
  getMasterDistrict,
  getMasterSubDistrict,
  getMasterProperty,
  getMasterZipcode,
  getMasterRetailType,
  getMasterTransportType,
  getMasterLocationType,
  getMasterDatasource,
  getMasterTransport,
  getMasterShippingBand,
  createMasterTransport,
  deleteMasterTransport,
  updateMasterTransport,
  getMasterTransportById,
  getMasterCancel,
  getMasterGeography,
  getMasterBankType,
  getMasterTagName,
  createMasterTagName,
  getMasterPoints,
  createMasterPoint,
  updateMasterPoint,
  deleteMasterPoint,
  addTagName,
  deleteTagName,
  getMasterBoxSize,
  createMasterData,
  deleteMasterData,
  updateMasterData,
  // MasterBoxes API functions (New System)
  getAllMasterBoxes,
  createMasterBox,
  updateMasterBox,
  deleteMasterBox,
  getMasterBoxById,
  getMasterPackagingType,
  getMasterPackagingTypeById,
  getMasterProductUnit,
  MasterProductUnitResponse,
} from 'services/masterDataApi'
// Province
export const useGetMasterProvince = (): UseQueryResult<
  MasterProvinceResponse,
  Error
> => {
  return useQuery<MasterProvinceResponse, Error>({
    queryKey: ['province'],
    queryFn: () => getMasterProvice(),
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  })
}

// District
export const useGetMasterDistrict = (
  provinceId: number
): UseQueryResult<MasterDistrictResponse, Error> => {
  return useQuery<MasterDistrictResponse, Error>({
    queryKey: ['province', provinceId],
    queryFn: () => getMasterDistrict(provinceId),
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  })
}

// Sub-District
export const useGetMasterSubDistrict = (
  districtId: number
): UseQueryResult<MasterSubDistrictResponse, Error> => {
  return useQuery<MasterSubDistrictResponse, Error>({
    queryKey: ['district', districtId],
    queryFn: () => getMasterSubDistrict(districtId),
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  })
}

// Property
export const useMasterProperty = (): UseQueryResult<
  MasterPropertyResponse,
  Error
> => {
  return useQuery<MasterPropertyResponse, Error>({
    queryKey: ['property'],
    queryFn: () => getMasterProperty(),
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  })
}

// MasterPackagingType
export const useGetMasterPackagingType = (ref_id?: string): UseQueryResult<MasterPackagingTypeResponse, Error> => {
  return useQuery<MasterPackagingTypeResponse, Error>({
    queryKey: ['packagingType', ref_id],
    queryFn: () => getMasterPackagingType(ref_id),
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  })
}

export const useGetMasterPackagingTypeById = (id?: string | null): UseQueryResult<MasterPackagingTypeSingleResponse, Error> => {
  return useQuery<MasterPackagingTypeSingleResponse, Error>({
    queryKey: ['packagingTypeById', id],
    queryFn: () => getMasterPackagingTypeById(id!),
    enabled: !!id,
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  })
}

export const useGetMasterProductUnit = (): UseQueryResult<MasterProductUnitResponse, Error> => {
  return useQuery<MasterProductUnitResponse, Error>({
    queryKey: ['productUnit'],
    queryFn: getMasterProductUnit,
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  })
}

// Zipcode
export const useGetZipcode = (
  zipCode: string
): UseQueryResult<GetMasterResponseZipCode, Error> => {
  return useQuery<GetMasterResponseZipCode, Error>({
    queryKey: ['zipCode', zipCode],
    queryFn: () => getMasterZipcode(zipCode),
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  })
}

// Retail Type
export const useGetRetailType = (): UseQueryResult<GetMasterRetailType, Error> => {
  return useQuery<GetMasterRetailType, Error>({
    queryKey: ['retailType'],
    queryFn: () => getMasterRetailType(),
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  })
}

// Transport Type
export const useGetTransportType = (): UseQueryResult<GetMasterTransportResponse, Error> => {
  return useQuery<GetMasterTransportResponse, Error>({
    queryKey: ['transportType'],
    queryFn: () => getMasterTransportType(),
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  })
}

// Location Type
export const useGetLocationType = (): UseQueryResult<GetMasterLocationResponse, Error> => {
  return useQuery<GetMasterLocationResponse, Error>({
    queryKey: ['locationType'],
    queryFn: () => getMasterLocationType(),
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  })
}

// Datasource
export const useGetDatasource = (): UseQueryResult<GetDatasourceListResponse, Error> => {
  return useQuery<GetDatasourceListResponse, Error>({
    queryKey: ['datasourceType'],
    queryFn: () => getMasterDatasource(),
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  })
}

// Master Transport
export const useMasterTransport = (
  input: MasterTransportInput
): UseQueryResult<MasterTransportListResponse, Error> => {
  return useQuery<MasterTransportListResponse, Error>({
    queryKey: ['load_master_transportation', input.search, input.parking_id],
    queryFn: () => getMasterTransport(input),
  })
}

// Shipping Band
export const useGetShippingBand = (): UseQueryResult<GetMasterShippingBandResponse, Error> => {
  return useQuery<GetMasterShippingBandResponse, Error>({
    queryKey: ['shippingBand'],
    queryFn: () => getMasterShippingBand(),
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  })
}

// Create Master Transport
export const useCreateMasterTransport = (): UseMutationResult<
  CreateMasterTransportResponse,
  Error,
  InputCreateMasterTransport,
  unknown
> => {
  return useMutation<CreateMasterTransportResponse, Error, InputCreateMasterTransport, unknown>({
    mutationFn: (input: InputCreateMasterTransport) => createMasterTransport(input),
  })
}

// Update Master Transport
export const useUpdateMasterTransport = (): UseMutationResult<
  UpdateMasterTransportResponse,
  Error,
  InputUpdateMasterTransport,
  unknown
> => {
  return useMutation<UpdateMasterTransportResponse, Error, InputUpdateMasterTransport, unknown>({
    mutationFn: (input: InputUpdateMasterTransport) => updateMasterTransport(input),
  })
}

// Delete Master Transport
export const useDeleteMasterTransport = (): UseMutationResult<
  DeleteMasterTransportResponse,
  Error,
  InputDeleteMasterTransport,
  unknown
> => {
  return useMutation<DeleteMasterTransportResponse, Error, InputDeleteMasterTransport, unknown>({
    mutationFn: (input: InputDeleteMasterTransport) => deleteMasterTransport(input),
  })
}

// Get Master Transport By Id
export const useGetMasterTransportById = (
  id: string
): UseQueryResult<MasterTransportByIdResponse, Error> => {
  return useQuery<MasterTransportByIdResponse, Error>({
    queryKey: ['getmaster_transport', id],
    queryFn: () => getMasterTransportById(id),
  })
}

// Get Master Transport By Id mutation
export const useGetMasterTransportByIdMutation = (): UseMutationResult<
  MasterTransportByIdResponse,
  Error,
  string,
  unknown
> => {
  return useMutation<MasterTransportByIdResponse, Error, string, unknown>({
    mutationFn: (id: string) => getMasterTransportById(id),
  })
}

// Master Cancel
export const useGetMasterCancel = (): UseQueryResult<MasterProcessResponse, Error> => {
  return useQuery<MasterProcessResponse, Error>({
    queryKey: ['cancelType'],
    queryFn: () => getMasterCancel(),
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  })
}

// Master Bank Type
export const useGetMasterBankType = (): UseQueryResult<MasterProcessResponse, Error> => {
  return useQuery<MasterProcessResponse, Error>({
    queryKey: ['bankType'],
    queryFn: () => getMasterBankType(),
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  })
}

// Geography
export const useGetMasterGeography = (): UseQueryResult<GeographyResponse, Error> => {
  return useQuery<GeographyResponse, Error>({
    queryKey: ['geographyType'],
    queryFn: () => getMasterGeography(),
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  })
}

// Tag Name
export const useGetMasterTagName = (factoryId: string): UseQueryResult<TagNameResponse, Error> => {
  return useQuery<TagNameResponse, Error>({
    queryKey: ['tagName'],
    queryFn: () => getMasterTagName(factoryId),
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  })
}

// Add Tag Name
export const useAddTagName = (): UseMutationResult<
  AddTagNamesResponse,
  Error,
  AddTagNamesInput,
  unknown
> => {
  return useMutation<AddTagNamesResponse, Error, AddTagNamesInput, unknown>({
    mutationFn: (input: AddTagNamesInput) => addTagName(input),
  })
}

// Create Tag Name
export const useCreateTagName = (): UseMutationResult<
  AddTagNamesResponse,
  Error,
  CreateTagNamesInput,
  unknown
> => {
  return useMutation<AddTagNamesResponse, Error, CreateTagNamesInput, unknown>({
    mutationFn: (input: CreateTagNamesInput) => createMasterTagName(input),
  })
}

//factory/tagname/delete
export const useDeleteTagName = (): UseMutationResult<
  AddTagNamesResponse,
  Error,
  DeleteTagNamesInput,
  unknown
> => {
  return useMutation<AddTagNamesResponse, Error, DeleteTagNamesInput, unknown>({
    mutationFn: (input: DeleteTagNamesInput) => deleteTagName(input),
  })
}

///master/points/get_all
export const useGetMasterPoint = (
  input: GetMasterPointInput
): UseQueryResult<GetMasterPointResponse, Error> => {
  return useQuery<GetMasterPointResponse, Error>({
    queryKey: ['get_master_point', input],
    queryFn: () => getMasterPoints(input),
  })
}

///master/points/create
export const useCreateMasterPoint = (): UseMutationResult<
  CreateMasterPointResponse,
  Error,
  CreateMasterPointInput,
  unknown
> => {
  return useMutation<CreateMasterPointResponse, Error, CreateMasterPointInput, unknown>({
    mutationFn: (input: CreateMasterPointInput) => createMasterPoint(input),
  })
}

//master/points/update
export const useUpdateMasterPoint = (): UseMutationResult<
  CreateMasterPointResponse,
  Error,
  UpdateMasterPointInput,
  unknown
> => {
  return useMutation<CreateMasterPointResponse, Error, UpdateMasterPointInput, unknown>({
    mutationFn: (input: UpdateMasterPointInput) => updateMasterPoint(input),
  })
}

//master/points/delete
export const useDeleteMasterPoint = (): UseMutationResult<
  CreateMasterPointResponse,
  Error,
  DeleteMasterPointInput,
  unknown
> => {
  return useMutation<CreateMasterPointResponse, Error, DeleteMasterPointInput, unknown>({
    mutationFn: (input: DeleteMasterPointInput) => deleteMasterPoint(input),
  })
}

///master/master_by_process/box_size
export const useMasterBoxSize = (): UseQueryResult<MasterBoxSizeResponse, Error> => {
  return useQuery<MasterBoxSizeResponse, Error>({
    queryKey: ['boxSize'],
    queryFn: () => getMasterBoxSize(),
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  })
}

///master/create_master_data
export const useCreateMasterData = (): UseMutationResult<
  MasterProcessResponse,
  Error,
  CreateMasterData,
  unknown
> => {
  return useMutation<MasterProcessResponse, Error, CreateMasterData, unknown>({
    mutationFn: (input: CreateMasterData) => createMasterData(input),
  })
}

///master/delete_master_data/41
export const useDeleteMasterData = (): UseMutationResult<
  MasterProcessResponse,
  Error,
  number,
  unknown
> => {
  return useMutation<MasterProcessResponse, Error, number, unknown>({
    mutationFn: (input: number) => deleteMasterData(input),
  })
}

///master/update_master_data
export const useUpdateMasterData = (): UseMutationResult<
  MasterProcessResponse,
  Error,
  UpdateMasterData,
  unknown
> => {
  return useMutation<MasterProcessResponse, Error, UpdateMasterData, unknown>({
    mutationFn: (input: UpdateMasterData) => updateMasterData(input),
  })
}

// ========================================
// MasterBoxes hooks (New System)
// ========================================

///master/boxes/getAll - Get All MasterBoxes
export const useMasterBoxes = (input?: GetAllMasterBoxesRequest): UseQueryResult<MasterBoxResponse, Error> => {
  console.log('🔄 [MasterBoxes Hook] useMasterBoxes called with input:', input);
  
  const query = useQuery<MasterBoxResponse, Error>({
    queryKey: ['masterBoxes', input],
    queryFn: () => getAllMasterBoxes(input || {}),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
  
  // Log success/error using useEffect
  React.useEffect(() => {
    if (query.data) {
      console.log('✅ [MasterBoxes Hook] useMasterBoxes success:', query.data);
    }
    if (query.error) {
      console.error('❌ [MasterBoxes Hook] useMasterBoxes error:', query.error);
    }
  }, [query.data, query.error]);
  
  return query;
}

///master/boxes - Create MasterBox
export const useCreateMasterBox = (): UseMutationResult<
  CreateMasterBoxResponse,
  Error,
  CreateMasterBoxRequest,
  unknown
> => {
  return useMutation<CreateMasterBoxResponse, Error, CreateMasterBoxRequest, unknown>({
    mutationFn: (input: CreateMasterBoxRequest) => {
      console.log('🔄 [MasterBoxes Hook] useCreateMasterBox called with input:', input);
      return createMasterBox(input);
    },
    onSuccess: (data) => {
      console.log('✅ [MasterBoxes Hook] useCreateMasterBox success:', data);
    },
    onError: (error) => {
      console.error('❌ [MasterBoxes Hook] useCreateMasterBox error:', error);
    }
  })
}

///master/boxes/update - Update MasterBox
export const useUpdateMasterBox = (): UseMutationResult<
  UpdateMasterBoxResponse,
  Error,
  UpdateMasterBoxRequest,
  unknown
> => {
  return useMutation<UpdateMasterBoxResponse, Error, UpdateMasterBoxRequest, unknown>({
    mutationFn: (input: UpdateMasterBoxRequest) => {
      console.log('🔄 [MasterBoxes Hook] useUpdateMasterBox called with input:', input);
      return updateMasterBox(input);
    },
    onSuccess: (data) => {
      console.log('✅ [MasterBoxes Hook] useUpdateMasterBox success:', data);
    },
    onError: (error) => {
      console.error('❌ [MasterBoxes Hook] useUpdateMasterBox error:', error);
    }
  })
}

///master/boxes/:id - Get MasterBox by ID
export const useMasterBoxById = (
  id: number | undefined, // ✅ แก้ไข: เปลี่ยนจาก string เป็น number เพื่อรองรับ SERIAL PRIMARY KEY
  options?: { enabled?: boolean }
): UseQueryResult<MasterBoxSingleResponse, Error> => {
  console.log('🔄 [MasterBoxes Hook] useMasterBoxById called with id:', id);
  
  const query = useQuery<MasterBoxSingleResponse, Error>({
    queryKey: ['masterBoxById', id],
    queryFn: () => {
      if (!id) {
        throw new Error('ID is required');
      }
      console.log('🚀 [MasterBoxes Hook] useMasterBoxById fetching data for id:', id);
      return getMasterBoxById(id);
    },
    enabled: options?.enabled !== false && !!id,
  });

  // Log success/error using useEffect pattern
  React.useEffect(() => {
    if (query.data) {
      console.log('✅ [MasterBoxes Hook] useMasterBoxById success:', query.data);
    }
    if (query.error) {
      console.error('❌ [MasterBoxes Hook] useMasterBoxById error:', query.error);
    }
  }, [query.data, query.error]);

  return query;
}

///master/boxes/delete - Delete MasterBox
export const useDeleteMasterBox = (): UseMutationResult<
  DeleteMasterBoxResponse,
  Error,
  DeleteMasterBoxRequest,
  unknown
> => {
  return useMutation<DeleteMasterBoxResponse, Error, DeleteMasterBoxRequest, unknown>({
    mutationFn: (input: DeleteMasterBoxRequest) => {
      console.log('🔄 [MasterBoxes Hook] useDeleteMasterBox called with input:', input);
      return deleteMasterBox(input);
    },
    onSuccess: (data) => {
      console.log('✅ [MasterBoxes Hook] useDeleteMasterBox success:', data);
    },
    onError: (error) => {
      console.error('❌ [MasterBoxes Hook] useDeleteMasterBox error:', error);
    }
  })
}















